import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Save, X, ImagePlus } from "lucide-react";
import { admin } from "../api";

const EMPTY = { label: "", cover: "", stories: [] };

export default function HighlightsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data } = await admin.get("/admin/highlights");
      setItems(data);
    } catch {
      toast.error("Failed to load highlights");
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function save(h) {
    setSaving(true);
    try {
      const payload = {
        label: h.label,
        cover: h.cover,
        stories: h.stories.filter((s) => s.image),
      };
      if (h._new) {
        await admin.post("/admin/highlights", payload);
        toast.success("Highlight created");
      } else {
        await admin.patch(`/admin/highlights/${h.id}`, payload);
        toast.success("Highlight updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this highlight?")) return;
    try {
      await admin.delete(`/admin/highlights/${id}`);
      toast.success("Highlight deleted");
      setEditing(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="font-body text-[13px] text-[#FDFBF7]/70">
          {items.length} highlights
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY, _new: true })}
          data-testid="admin-highlight-new"
          className="pill pill-primary"
        >
          <Plus className="h-4 w-4" /> New highlight
        </button>
      </div>

      <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {items.map((h) => (
          <button
            key={h.id}
            onClick={() => setEditing(h)}
            data-testid={`admin-highlight-${h.id}`}
            className="group flex flex-col items-center gap-3"
          >
            <div className="gold-ring w-[80px] transition-transform duration-500 group-hover:scale-[1.05]">
              <div className="overflow-hidden rounded-full bg-[#1A050B]">
                <img src={h.cover} alt={h.label} className="aspect-square w-full object-cover" />
              </div>
            </div>
            <div className="font-body text-[12px] uppercase tracking-[0.18em] text-[#FDFBF7]/80 group-hover:text-[#D4AF37] transition-colors">
              {h.label}
            </div>
            <div className="font-body text-[10px] text-[#FDFBF7]/40">
              {h.stories.length} stories
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {editing && (
          <HighlightEditor
            key={editing.id || "new"}
            highlight={editing}
            onCancel={() => setEditing(null)}
            onSave={save}
            onDelete={editing.id && !editing._new ? () => remove(editing.id) : null}
            saving={saving}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function HighlightEditor({ highlight, onCancel, onSave, onDelete, saving }) {
  const [form, setForm] = useState({
    ...highlight,
    stories: [...(highlight.stories || [])],
  });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function updStory(i, k, v) {
    setForm((f) => {
      const stories = [...f.stories];
      stories[i] = { ...stories[i], [k]: v };
      return { ...f, stories };
    });
  }
  function addStory() {
    setForm((f) => ({ ...f, stories: [...f.stories, { image: "", caption: "" }] }));
  }
  function removeStory(i) {
    setForm((f) => ({ ...f, stories: f.stories.filter((_, idx) => idx !== i) }));
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1000] flex items-center justify-center overflow-y-auto bg-[#1A050B]/95 backdrop-blur-md p-4 md:p-8"
      onClick={onCancel}
    >
      <motion.form
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          onSave(form);
        }}
        className="relative my-8 w-full max-w-3xl border border-[#D4AF37]/30 bg-[#4A0E1F] p-8 md:p-10"
      >
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-4 top-4 rounded-full p-2 text-[#FDFBF7]/70 hover:bg-white/10 hover:text-[#FDFBF7] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-3 flex items-center gap-3">
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span className="font-body text-[11px] uppercase tracking-[0.36em] text-[#D4AF37]">
            {highlight._new ? "New highlight" : `Edit · ${highlight.id}`}
          </span>
        </div>
        <h2 className="mb-8 font-serif text-3xl leading-none">Compose a chapter.</h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <label className="block">
            <span className="line-label">Label</span>
            <input required value={form.label} onChange={upd("label")} className="line-input" data-testid="admin-highlight-label" />
          </label>
          <label className="block">
            <span className="line-label">Cover image URL</span>
            <input required value={form.cover} onChange={upd("cover")} className="line-input" data-testid="admin-highlight-cover" />
          </label>
        </div>

        <div className="mt-10 border-t border-[#D4AF37]/25 pt-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="font-body text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
              Stories ({form.stories.length})
            </div>
            <button type="button" onClick={addStory} className="pill pill-ghost" data-testid="admin-story-add">
              <ImagePlus className="h-4 w-4" /> Add story
            </button>
          </div>
          <div className="space-y-4">
            {form.stories.map((s, i) => (
              <div key={i} className="grid grid-cols-[80px_1fr_auto] gap-4 border border-[#D4AF37]/20 p-3">
                {s.image ? (
                  <img src={s.image} alt="" className="aspect-square h-20 w-20 object-cover" />
                ) : (
                  <div className="h-20 w-20 border border-dashed border-[#D4AF37]/30" />
                )}
                <div className="space-y-2">
                  <input
                    value={s.image}
                    onChange={(e) => updStory(i, "image", e.target.value)}
                    placeholder="Image URL"
                    className="line-input py-2"
                  />
                  <input
                    value={s.caption}
                    onChange={(e) => updStory(i, "caption", e.target.value)}
                    placeholder="Caption"
                    className="line-input py-2"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeStory(i)}
                  className="self-start rounded-full p-2 text-[#FDFBF7]/50 hover:text-[#F5D97A] transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {form.stories.length === 0 && (
              <div className="border border-dashed border-[#D4AF37]/30 py-8 text-center font-body text-[13px] text-[#FDFBF7]/50">
                No stories yet — add one above.
              </div>
            )}
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between">
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="pill pill-ghost text-[#F5D97A]/80 hover:text-[#F5D97A]"
              data-testid="admin-highlight-delete"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onCancel} className="pill pill-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="pill pill-primary" data-testid="admin-highlight-save">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </motion.form>
    </motion.div>
  );
}
